import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../utils/axiosConfig';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8083/api';

// Async thunks for customer operations

// Fetch customer profile
export const fetchCustomerProfile = createAsyncThunk(
  'customer/fetchCustomerProfile',
  async (customerId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/users/${customerId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch customer profile');
    }
  }
);

// Update customer profile
export const updateCustomerProfile = createAsyncThunk(
  'customer/updateCustomerProfile',
  async ({ customerId, profileData }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${API_BASE_URL}/users/${customerId}`, profileData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update customer profile');
    }
  }
);

// Fetch customer orders
export const fetchCustomerOrders = createAsyncThunk(
  'customer/fetchCustomerOrders',
  async ({ customerId, page = 0, size = 10, status }, { rejectWithValue }) => {
    try {
      const params = { page, size };
      if (status) params.status = status;
      
      const response = await axios.get(`${API_BASE_URL}/orders/customer/${customerId}`, {
        params,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch customer orders');
    }
  }
);

// Track order
export const trackOrder = createAsyncThunk(
  'customer/trackOrder',
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/orders/${orderId}/tracking`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to track order');
    }
  }
);

// Cancel order
export const cancelOrder = createAsyncThunk(
  'customer/cancelOrder',
  async ({ orderId, reason }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/orders/${orderId}/cancel`, 
        { reason },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      return { orderId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to cancel order');
    }
  }
);

// Fetch customer wishlist
export const fetchCustomerWishlist = createAsyncThunk(
  'customer/fetchCustomerWishlist',
  async (customerId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/wishlist/${customerId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch wishlist');
    }
  }
);

// Add to wishlist
export const addToWishlist = createAsyncThunk(
  'customer/addToWishlist',
  async ({ customerId, productId }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/wishlist/${customerId}/add/${productId}`, 
        {},
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add to wishlist');
    }
  }
);

// Remove from wishlist
export const removeFromWishlist = createAsyncThunk(
  'customer/removeFromWishlist',
  async ({ customerId, productId }, { rejectWithValue }) => {
    try {
      const response = await axios.delete(`${API_BASE_URL}/wishlist/${customerId}/remove/${productId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      return { productId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to remove from wishlist');
    }
  }
);

// Check if product is in wishlist
export const checkProductInWishlist = createAsyncThunk(
  'customer/checkProductInWishlist',
  async ({ customerId, productId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/wishlist/${customerId}/contains/${productId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to check wishlist');
    }
  }
);

// Get wishlist items count
export const getWishlistItemsCount = createAsyncThunk(
  'customer/getWishlistItemsCount',
  async (customerId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/wishlist/${customerId}/count`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to get wishlist count');
    }
  }
);

// Clear wishlist
export const clearWishlist = createAsyncThunk(
  'customer/clearWishlist',
  async (customerId, { rejectWithValue }) => {
    try {
      const response = await axios.delete(`${API_BASE_URL}/wishlist/${customerId}/clear`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to clear wishlist');
    }
  }
);

// Move product from wishlist to cart
export const moveToCartFromWishlist = createAsyncThunk(
  'customer/moveToCartFromWishlist',
  async ({ customerId, productId, quantity = 1 }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/wishlist/${customerId}/move-to-cart/${productId}`, 
        {},
        {
          params: { quantity },
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      return { productId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to move to cart');
    }
  }
);

// Fetch customer cart
export const fetchCustomerCart = createAsyncThunk(
  'customer/fetchCustomerCart',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/cart`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch cart');
    }
  }
);

// Add to cart
export const addToCart = createAsyncThunk(
  'customer/addToCart',
  async ({ customerId, productId, quantity = 1 }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/cart/add`, 
        { customerId, productId, quantity },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add to cart');
    }
  }
);

// Update cart item
export const updateCartItem = createAsyncThunk(
  'customer/updateCartItem',
  async ({ cartId, productId, quantity }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${API_BASE_URL}/cart/${cartId}/items/${productId}`, 
        { quantity },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update cart item');
    }
  }
);

// Remove from cart
export const removeFromCart = createAsyncThunk(
  'customer/removeFromCart',
  async ({ cartId, productId }, { rejectWithValue }) => {
    try {
      const response = await axios.delete(`${API_BASE_URL}/cart/${cartId}/items/${productId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      return { productId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to remove from cart');
    }
  }
);

// Clear cart
export const clearCart = createAsyncThunk(
  'customer/clearCart',
  async (cartId, { rejectWithValue }) => {
    try {
      const response = await axios.delete(`${API_BASE_URL}/cart/${cartId}/clear`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to clear cart');
    }
  }
);

// Fetch customer payments
export const fetchCustomerPayments = createAsyncThunk(
  'customer/fetchCustomerPayments',
  async ({ customerId, page = 0, size = 10 }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/payments/customer/${customerId}`, {
        params: { page, size },
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch customer payments');
    }
  }
);

// Create review
export const createReview = createAsyncThunk(
  'customer/createReview',
  async (reviewData, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/reviews`, reviewData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create review');
    }
  }
);

// Fetch customer reviews
export const fetchCustomerReviews = createAsyncThunk(
  'customer/fetchCustomerReviews',
  async ({ customerId, page = 0, size = 10 }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/reviews/customer/${customerId}`, {
        params: { page, size },
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch customer reviews');
    }
  }
);

// Update review
export const updateReview = createAsyncThunk(
  'customer/updateReview',
  async ({ reviewId, reviewData }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${API_BASE_URL}/reviews/${reviewId}`, reviewData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update review');
    }
  }
);

// Delete review
export const deleteReview = createAsyncThunk(
  'customer/deleteReview',
  async (reviewId, { rejectWithValue }) => {
    try {
      const response = await axios.delete(`${API_BASE_URL}/reviews/${reviewId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      return { reviewId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete review');
    }
  }
);

// Fetch customer addresses
export const fetchCustomerAddresses = createAsyncThunk(
  'customer/fetchCustomerAddresses',
  async (customerId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/users/${customerId}/addresses`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch addresses');
    }
  }
);

// Add customer address
export const addCustomerAddress = createAsyncThunk(
  'customer/addCustomerAddress',
  async ({ customerId, addressData }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/users/${customerId}/addresses`, addressData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add address');
    }
  }
);

// Update customer address
export const updateCustomerAddress = createAsyncThunk(
  'customer/updateCustomerAddress',
  async ({ customerId, addressId, addressData }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${API_BASE_URL}/users/${customerId}/addresses/${addressId}`, addressData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update address');
    }
  }
);

// Delete customer address
export const deleteCustomerAddress = createAsyncThunk(
  'customer/deleteCustomerAddress',
  async ({ customerId, addressId }, { rejectWithValue }) => {
    try {
      const response = await axios.delete(`${API_BASE_URL}/users/${customerId}/addresses/${addressId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      return { addressId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete address');
    }
  }
);

const initialState = {
  // Profile
  profile: null,
  profileLoading: false,
  profileError: null,
  
  // Orders
  orders: [],
  ordersLoading: false,
  ordersError: null,
  ordersPagination: {
    currentPage: 0,
    totalPages: 0,
    totalItems: 0,
    hasNext: false,
    hasPrevious: false
  },
  
  // Order tracking
  trackingInfo: null,
  trackingLoading: false,
  trackingError: null,
  
  // Wishlist
  wishlist: {
    items: [],
    itemCount: 0,
    customerId: null,
    createdAt: null,
    updatedAt: null
  },
  wishlistLoading: false,
  wishlistError: null,
  wishlistItemsCount: 0,
  
  // Cart
  cart: null,
  cartLoading: false,
  cartError: null,
  
  // Payments
  payments: [],
  paymentsLoading: false,
  paymentsError: null,
  paymentsPagination: {
    currentPage: 0,
    totalPages: 0,
    totalItems: 0,
    hasNext: false,
    hasPrevious: false
  },
  
  // Reviews
  reviews: [],
  reviewsLoading: false,
  reviewsError: null,
  reviewsPagination: {
    currentPage: 0,
    totalPages: 0,
    totalItems: 0,
    hasNext: false,
    hasPrevious: false
  },
  
  // Addresses
  addresses: [],
  addressesLoading: false,
  addressesError: null,
  
  // General
  loading: false,
  error: null
};

const customerSlice = createSlice({
  name: 'customer',
  initialState,
  reducers: {
    clearCustomerData: (state) => {
      return initialState;
    },
    clearCustomerError: (state) => {
      state.error = null;
      state.profileError = null;
      state.ordersError = null;
      state.trackingError = null;
      state.wishlistError = null;
      state.cartError = null;
      state.paymentsError = null;
      state.reviewsError = null;
      state.addressesError = null;
    },
    updateCartItemQuantity: (state, action) => {
      const { productId, quantity } = action.payload;
      if (state.cart && state.cart.items) {
        const item = state.cart.items.find(item => item.productId === productId);
        if (item) {
          item.quantity = quantity;
        }
      }
    },
    removeCartItem: (state, action) => {
      const productId = action.payload;
      if (state.cart && state.cart.items) {
        state.cart.items = state.cart.items.filter(item => item.productId !== productId);
      }
    },
    removeWishlistItem: (state, action) => {
      const productId = action.payload;
      if (state.wishlist.items) {
        state.wishlist.items = state.wishlist.items.filter(item => item.productId !== productId);
        state.wishlist.itemCount = state.wishlist.items.length;
        state.wishlistItemsCount = state.wishlist.items.length;
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Customer Profile
      .addCase(fetchCustomerProfile.pending, (state) => {
        state.profileLoading = true;
        state.profileError = null;
      })
      .addCase(fetchCustomerProfile.fulfilled, (state, action) => {
        state.profileLoading = false;
        state.profile = action.payload;
      })
      .addCase(fetchCustomerProfile.rejected, (state, action) => {
        state.profileLoading = false;
        state.profileError = action.payload;
      })
      
      // Update Profile
      .addCase(updateCustomerProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCustomerProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload;
      })
      .addCase(updateCustomerProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Customer Orders
      .addCase(fetchCustomerOrders.pending, (state) => {
        state.ordersLoading = true;
        state.ordersError = null;
      })
      .addCase(fetchCustomerOrders.fulfilled, (state, action) => {
        state.ordersLoading = false;
        state.orders = action.payload.orders || action.payload.content || [];
        state.ordersPagination = {
          currentPage: action.payload.currentPage || 0,
          totalPages: action.payload.totalPages || 0,
          totalItems: action.payload.totalItems || 0,
          hasNext: action.payload.hasNext || false,
          hasPrevious: action.payload.hasPrevious || false
        };
      })
      .addCase(fetchCustomerOrders.rejected, (state, action) => {
        state.ordersLoading = false;
        state.ordersError = action.payload;
      })
      
      // Track Order
      .addCase(trackOrder.pending, (state) => {
        state.trackingLoading = true;
        state.trackingError = null;
      })
      .addCase(trackOrder.fulfilled, (state, action) => {
        state.trackingLoading = false;
        state.trackingInfo = action.payload;
      })
      .addCase(trackOrder.rejected, (state, action) => {
        state.trackingLoading = false;
        state.trackingError = action.payload;
      })
      
      // Cancel Order
      .addCase(cancelOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cancelOrder.fulfilled, (state, action) => {
        state.loading = false;
        // Update the order status in the orders array
        const orderIndex = state.orders.findIndex(order => order.id === action.payload.orderId);
        if (orderIndex !== -1) {
          state.orders[orderIndex].status = 'CANCELLED';
        }
      })
      .addCase(cancelOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Wishlist
      .addCase(fetchCustomerWishlist.pending, (state) => {
        state.wishlistLoading = true;
        state.wishlistError = null;
      })
      .addCase(fetchCustomerWishlist.fulfilled, (state, action) => {
        state.wishlistLoading = false;
        state.wishlist = action.payload.wishlist || action.payload;
        state.wishlistItemsCount = action.payload.itemCount || action.payload.items?.length || 0;
      })
      .addCase(fetchCustomerWishlist.rejected, (state, action) => {
        state.wishlistLoading = false;
        state.wishlistError = action.payload;
      })
      
      // Add to Wishlist
      .addCase(addToWishlist.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addToWishlist.fulfilled, (state, action) => {
        state.loading = false;
        state.wishlist = action.payload.wishlist || action.payload;
        state.wishlistItemsCount = action.payload.itemCount || action.payload.wishlist?.itemCount || 0;
      })
      .addCase(addToWishlist.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Remove from Wishlist
      .addCase(removeFromWishlist.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeFromWishlist.fulfilled, (state, action) => {
        state.loading = false;
        state.wishlist = action.payload.data.wishlist || action.payload.data;
        state.wishlistItemsCount = action.payload.data.itemCount || action.payload.data.wishlist?.itemCount || 0;
      })
      .addCase(removeFromWishlist.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Check Product in Wishlist
      .addCase(checkProductInWishlist.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkProductInWishlist.fulfilled, (state, action) => {
        state.loading = false;
        // This is just for checking, no state update needed
      })
      .addCase(checkProductInWishlist.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Get Wishlist Items Count
      .addCase(getWishlistItemsCount.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getWishlistItemsCount.fulfilled, (state, action) => {
        state.loading = false;
        state.wishlistItemsCount = action.payload.count || 0;
      })
      .addCase(getWishlistItemsCount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Clear Wishlist
      .addCase(clearWishlist.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(clearWishlist.fulfilled, (state, action) => {
        state.loading = false;
        state.wishlist = action.payload.wishlist || { items: [], itemCount: 0 };
        state.wishlistItemsCount = 0;
      })
      .addCase(clearWishlist.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Move to Cart from Wishlist
      .addCase(moveToCartFromWishlist.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(moveToCartFromWishlist.fulfilled, (state, action) => {
        state.loading = false;
        // Remove the item from wishlist since it was moved to cart
        if (state.wishlist.items) {
          state.wishlist.items = state.wishlist.items.filter(item => item.productId !== action.payload.productId);
          state.wishlist.itemCount = state.wishlist.items.length;
          state.wishlistItemsCount = state.wishlist.items.length;
        }
      })
      .addCase(moveToCartFromWishlist.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Cart
      .addCase(fetchCustomerCart.pending, (state) => {
        state.cartLoading = true;
        state.cartError = null;
      })
      .addCase(fetchCustomerCart.fulfilled, (state, action) => {
        state.cartLoading = false;
        state.cart = action.payload;
      })
      .addCase(fetchCustomerCart.rejected, (state, action) => {
        state.cartLoading = false;
        state.cartError = action.payload;
      })
      
      // Add to Cart
      .addCase(addToCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addToCart.fulfilled, (state, action) => {
        state.loading = false;
        state.cart = action.payload;
      })
      .addCase(addToCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update Cart Item
      .addCase(updateCartItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCartItem.fulfilled, (state, action) => {
        state.loading = false;
        state.cart = action.payload;
      })
      .addCase(updateCartItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Remove from Cart
      .addCase(removeFromCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeFromCart.fulfilled, (state, action) => {
        state.loading = false;
        if (state.cart && state.cart.items) {
          state.cart.items = state.cart.items.filter(item => item.productId !== action.payload.productId);
        }
      })
      .addCase(removeFromCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Clear Cart
      .addCase(clearCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(clearCart.fulfilled, (state, action) => {
        state.loading = false;
        state.cart = null;
      })
      .addCase(clearCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Customer Payments
      .addCase(fetchCustomerPayments.pending, (state) => {
        state.paymentsLoading = true;
        state.paymentsError = null;
      })
      .addCase(fetchCustomerPayments.fulfilled, (state, action) => {
        state.paymentsLoading = false;
        state.payments = action.payload.payments || action.payload.content || [];
        state.paymentsPagination = {
          currentPage: action.payload.currentPage || 0,
          totalPages: action.payload.totalPages || 0,
          totalItems: action.payload.totalItems || 0,
          hasNext: action.payload.hasNext || false,
          hasPrevious: action.payload.hasPrevious || false
        };
      })
      .addCase(fetchCustomerPayments.rejected, (state, action) => {
        state.paymentsLoading = false;
        state.paymentsError = action.payload;
      })
      
      // Customer Reviews
      .addCase(fetchCustomerReviews.pending, (state) => {
        state.reviewsLoading = true;
        state.reviewsError = null;
      })
      .addCase(fetchCustomerReviews.fulfilled, (state, action) => {
        state.reviewsLoading = false;
        state.reviews = action.payload.reviews || action.payload.content || [];
        state.reviewsPagination = {
          currentPage: action.payload.currentPage || 0,
          totalPages: action.payload.totalPages || 0,
          totalItems: action.payload.totalItems || 0,
          hasNext: action.payload.hasNext || false,
          hasPrevious: action.payload.hasPrevious || false
        };
      })
      .addCase(fetchCustomerReviews.rejected, (state, action) => {
        state.reviewsLoading = false;
        state.reviewsError = action.payload;
      })
      
      // Create Review
      .addCase(createReview.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createReview.fulfilled, (state, action) => {
        state.loading = false;
        state.reviews.unshift(action.payload);
      })
      .addCase(createReview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update Review
      .addCase(updateReview.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateReview.fulfilled, (state, action) => {
        state.loading = false;
        const reviewIndex = state.reviews.findIndex(review => review.id === action.payload.id);
        if (reviewIndex !== -1) {
          state.reviews[reviewIndex] = action.payload;
        }
      })
      .addCase(updateReview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Delete Review
      .addCase(deleteReview.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteReview.fulfilled, (state, action) => {
        state.loading = false;
        state.reviews = state.reviews.filter(review => review.id !== action.payload.reviewId);
      })
      .addCase(deleteReview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Customer Addresses
      .addCase(fetchCustomerAddresses.pending, (state) => {
        state.addressesLoading = true;
        state.addressesError = null;
      })
      .addCase(fetchCustomerAddresses.fulfilled, (state, action) => {
        state.addressesLoading = false;
        state.addresses = action.payload.addresses || action.payload || [];
      })
      .addCase(fetchCustomerAddresses.rejected, (state, action) => {
        state.addressesLoading = false;
        state.addressesError = action.payload;
      })
      
      // Add Address
      .addCase(addCustomerAddress.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addCustomerAddress.fulfilled, (state, action) => {
        state.loading = false;
        state.addresses.push(action.payload);
      })
      .addCase(addCustomerAddress.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update Address
      .addCase(updateCustomerAddress.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCustomerAddress.fulfilled, (state, action) => {
        state.loading = false;
        const addressIndex = state.addresses.findIndex(address => address.id === action.payload.id);
        if (addressIndex !== -1) {
          state.addresses[addressIndex] = action.payload;
        }
      })
      .addCase(updateCustomerAddress.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Delete Address
      .addCase(deleteCustomerAddress.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCustomerAddress.fulfilled, (state, action) => {
        state.loading = false;
        state.addresses = state.addresses.filter(address => address.id !== action.payload.addressId);
      })
      .addCase(deleteCustomerAddress.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Reset all customer data (wishlist count, etc.) when user logs out
      .addCase('auth/logoutUser/fulfilled', (state) => {
        return initialState;
      });
  }
});

export const { 
  clearCustomerData, 
  clearCustomerError, 
  updateCartItemQuantity, 
  removeCartItem, 
  removeWishlistItem 
} = customerSlice.actions;

export default customerSlice.reducer;